import { Box, Panel, spacing } from "@procore/core-react";
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useTranslation } from "react-i18next";
import { Rnd } from "react-rnd";

import { ELEMENT_IDS } from "features/bookmarks/utils";

import { EmptyState } from "./emptyState";
import { LoadingState } from "./loadingState";
import { calculateWindowPosition, WindowPosition } from "./windowPositioning";
import { useWindowRenderMode } from "./windowRenderModeContext";

export const VIEWER_INSET = spacing.sm;

function horizontalAnchorFromEdgeGaps(distFromLeftInset: number, rightGap: number): "left" | "right" {
    return distFromLeftInset <= rightGap ? "left" : "right";
}

function verticalAnchorFromEdgeGaps(distFromTopInset: number, bottomGap: number): "top" | "bottom" {
    return distFromTopInset <= bottomGap ? "top" : "bottom";
}

function computeEdgeMetrics(
    pos: { x: number; y: number },
    containerSize: { width: number; height: number },
    windowSize: { width: number; height: number },
) {
    const rightGap = containerSize.width - VIEWER_INSET - windowSize.width - pos.x;
    const bottomGap = containerSize.height - VIEWER_INSET - windowSize.height - pos.y;
    return {
        rightGap,
        bottomGap,
        horizontalAnchor: horizontalAnchorFromEdgeGaps(pos.x - VIEWER_INSET, rightGap),
        verticalAnchor: verticalAnchorFromEdgeGaps(pos.y - VIEWER_INSET, bottomGap),
    };
}

function getRenderContainerRect(): { left: number; top: number; width: number; height: number } {
    const container = document.getElementById(ELEMENT_IDS.renderContainer);
    if (container) {
        const r = container.getBoundingClientRect();
        return { left: r.left, top: r.top, width: r.width, height: r.height };
    }
    return { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight };
}

export interface EmptyStateConfig {
    title: string;
    message: string;
}

export interface WindowProps {
    children: ReactNode;
    onClose?: () => void;
    onFocus?: () => void;
    header?: ReactNode | string;
    position?: WindowPosition;
    size?: { width: number; height: number };
    minSize?: { width?: number; height?: number };
    loading?: boolean;
    loadingMessage?: string;
    error?: string | null;
    emptyState?: EmptyStateConfig;
    secondaryBgColor?: boolean;
    autoHeight?: boolean;
    "data-testid"?: string;
}

function buildHeaderElement(header: WindowProps["header"], onClose?: () => void) {
    if (!header) {
        return null;
    }
    return (
        <div className="window-header">
            {typeof header === "string" ? (
                <Panel.Header onClose={onClose}>
                    <Panel.Title>{header}</Panel.Title>
                </Panel.Header>
            ) : (
                header
            )}
        </div>
    );
}

export function Window(props: WindowProps) {
    const renderMode = useWindowRenderMode();
    if (renderMode === "contentOnly") {
        return (
            <>
                {buildHeaderElement(props.header, props.onClose)}
                {props.children}
            </>
        );
    }
    return <WindowFull {...props} />;
}

function WindowFull({
    children,
    onClose,
    onFocus,
    header,
    position,
    size = { width: 352, height: 645 },
    minSize = { width: 352, height: 625 },
    loading = false,
    loadingMessage,
    error = null,
    emptyState,
    secondaryBgColor = false,
    autoHeight = false,
    "data-testid": dataTestId,
}: WindowProps) {
    const { t } = useTranslation();
    const isDraggingRef = useRef(false);

    const effectiveLoadingMessage = loadingMessage || t("loading");

    const { width: minWidth = 352, height: minHeight = 625 } = minSize;

    const effectiveSize = {
        width: Math.max(size.width, minWidth),
        height: Math.max(size.height, minHeight),
    };

    const getContainerSize = useCallback(() => {
        const container = document.getElementById(ELEMENT_IDS.renderContainer);
        if (container) {
            return { width: container.clientWidth, height: container.clientHeight };
        }
        return { width: window.innerWidth, height: window.innerHeight };
    }, []);

    const clampToContainer = useCallback(
        (
            x: number,
            y: number,
            containerWidth: number,
            containerHeight: number,
            windowSize: { width: number; height: number },
        ) => {
            const minX = VIEWER_INSET;
            const maxX = Math.max(minX, containerWidth - windowSize.width - VIEWER_INSET);
            const minY = VIEWER_INSET;
            const maxY = Math.max(minY, containerHeight - windowSize.height - VIEWER_INSET);
            return {
                x: Math.max(minX, Math.min(x, maxX)),
                y: Math.max(minY, Math.min(y, maxY)),
            };
        },
        [],
    );

    const containerRect = getRenderContainerRect();
    const initialContainerSize = { width: containerRect.width, height: containerRect.height };

    const windowPos = calculateWindowPosition(position, effectiveSize, containerRect);

    const [currentSize, setCurrentSize] = useState(effectiveSize);
    const currentSizeRef = useRef(currentSize);
    currentSizeRef.current = currentSize;

    const initial = clampToContainer(
        windowPos.x,
        windowPos.y,
        initialContainerSize.width,
        initialContainerSize.height,
        effectiveSize,
    );
    const [currentPos, setCurrentPos] = useState(initial);

    const initialMetrics = computeEdgeMetrics(initial, initialContainerSize, effectiveSize);
    const horizontalAnchorRef = useRef<"left" | "right">(initialMetrics.horizontalAnchor);
    const verticalAnchorRef = useRef<"top" | "bottom">(initialMetrics.verticalAnchor);
    const rightGapRef = useRef(initialMetrics.rightGap);
    const bottomGapRef = useRef(initialMetrics.bottomGap);

    useEffect(() => {
        const container = document.getElementById(ELEMENT_IDS.renderContainer);
        if (!container) {
            return;
        }

        const observer = new ResizeObserver(() => {
            const newW = container.clientWidth;
            const newH = container.clientHeight;

            if (isDraggingRef.current) {
                return;
            }

            flushSync(() => {
                setCurrentPos((prev) => {
                    const ws = currentSizeRef.current;
                    const newX =
                        horizontalAnchorRef.current === "left"
                            ? prev.x
                            : newW - VIEWER_INSET - ws.width - rightGapRef.current;

                    const newY =
                        verticalAnchorRef.current === "top"
                            ? prev.y
                            : newH - VIEWER_INSET - ws.height - bottomGapRef.current;

                    return clampToContainer(newX, newY, newW, newH, ws);
                });
            });
        });

        observer.observe(container);
        return () => observer.disconnect();
    }, [clampToContainer]);

    const headerElement = buildHeaderElement(header, onClose);

    const renderContent = () => {
        // Priority order: error > loading > emptyState
        const hasOverlay = error || loading || emptyState;

        const overlayStyle: React.CSSProperties = {
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
        };

        return (
            <div style={{ position: "relative", height: "100%" }}>
                {/* Always render children, but hide when overlay is shown */}
                <div style={{ display: hasOverlay ? "none" : "block", height: "100%" }}>{children}</div>

                {/* Render only the highest priority overlay */}
                {error ? (
                    <div style={overlayStyle}>
                        <EmptyState title={t("error")} message={error} />
                    </div>
                ) : loading ? (
                    <div style={overlayStyle}>
                        <LoadingState message={effectiveLoadingMessage} />
                    </div>
                ) : emptyState ? (
                    <div style={overlayStyle}>
                        <EmptyState title={emptyState.title} message={emptyState.message} />
                    </div>
                ) : null}
            </div>
        );
    };

    return (
        <Rnd
            position={{ x: currentPos.x - VIEWER_INSET, y: currentPos.y - VIEWER_INSET }}
            size={{ width: currentSize.width, height: autoHeight ? "auto" : currentSize.height }}
            minWidth={minWidth}
            minHeight={autoHeight ? undefined : minHeight}
            bounds="parent"
            dragHandleClassName="window-header"
            cancel="input, textarea, select, [contenteditable]"
            style={{
                pointerEvents: "auto",
            }}
            onDragStart={() => {
                isDraggingRef.current = true;
                onFocus?.();
            }}
            onDragStop={(_, d) => {
                isDraggingRef.current = false;
                const { width, height } = getContainerSize();
                const clamped = clampToContainer(d.x + VIEWER_INSET, d.y + VIEWER_INSET, width, height, currentSize);
                setCurrentPos(clamped);
                const metrics = computeEdgeMetrics(clamped, { width, height }, currentSize);
                rightGapRef.current = metrics.rightGap;
                bottomGapRef.current = metrics.bottomGap;
                horizontalAnchorRef.current = metrics.horizontalAnchor;
                verticalAnchorRef.current = metrics.verticalAnchor;
            }}
            onResizeStop={(_e, _dir, elementRef, _delta, pos) => {
                const nextW = Math.max(elementRef.offsetWidth, minWidth);
                const nextH = Math.max(elementRef.offsetHeight, minHeight);
                const newSize = { width: nextW, height: nextH };
                currentSizeRef.current = newSize;
                setCurrentSize(newSize);
                const { width: cw, height: ch } = getContainerSize();
                const clamped = clampToContainer(pos.x + VIEWER_INSET, pos.y + VIEWER_INSET, cw, ch, newSize);
                setCurrentPos(clamped);
                const metrics = computeEdgeMetrics(clamped, { width: cw, height: ch }, newSize);
                rightGapRef.current = metrics.rightGap;
                bottomGapRef.current = metrics.bottomGap;
                horizontalAnchorRef.current = metrics.horizontalAnchor;
                verticalAnchorRef.current = metrics.verticalAnchor;
            }}
        >
            <Panel
                style={{
                    boxShadow: "2px 0px 6px 0px rgba(0, 0, 0, 0.1)",
                    borderRadius: "8px",
                    ...(autoHeight ? { maxHeight: "80vh", display: "flex", flexDirection: "column" } : {}),
                }}
                data-testid={dataTestId}
            >
                {headerElement}
                <Panel.Body secondaryBgColor={secondaryBgColor}>
                    <Box
                        style={{
                            overflow: "auto",
                            width: "100%",
                            height: autoHeight ? undefined : "100%",
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        {renderContent()}
                    </Box>
                </Panel.Body>
            </Panel>
        </Rnd>
    );
}
